import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AdminProfileDto,
  AdminProfileSearchDto,
  AdminProfileDetailDto,
  SendNotificationDto,
  SendNotificationResponseDto,
  AdminNotificationDto,
  NotificationHistorySearchDto,
  ContractorAdminDto,
  ContractorAdminSearchDto,
  ContractorAdminDetailDto,
  ContractorStatusUpdateResultDto,
  UpdateStatusDto,
  CreateAdminNoteDto,
  AdminNoteDto,
  ResetPasswordDto,
  ImpersonationResponseDto,
  FacilityForAdminDto,
  FacilityAdminSearchDto,
  FacilityAdminDetailDto,
  AdminAlertDto,
  VerificationTaskDto,
  ReviewVerificationTaskDto,
  VerifyContractorDto,
  ContractorVerificationResultDto,
  ApproveFacilityOnboardingDto,
  ApproveFacilityOnboardingResponseDto
} from '../dtos';
import { PagedResultDto } from '@umss/shared/data-access';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly base = '/api/admin';

  constructor(private http: HttpClient) {}

  private toUtcIsoString(
    localDateTimeString: string | null | undefined
  ): string | undefined {
    if (!localDateTimeString) return undefined;

    try {
      const date = new Date(localDateTimeString);

      return date.toISOString();
    } catch {
      return undefined;
    }
  }

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return httpParams;
  }

  getAllAdminProfiles(
    searchParams: AdminProfileSearchDto = {}
  ): Observable<PagedResultDto<AdminProfileDto>> {
    let params = new HttpParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<PagedResultDto<AdminProfileDto>>(
      `${this.base}/profiles`,
      { params }
    );
  }

  getAdminProfileById(id: string): Observable<AdminProfileDetailDto> {
    return this.http.get<AdminProfileDetailDto>(`${this.base}/profiles/${id}`);
  }

  sendNotification(
    body: SendNotificationDto
  ): Observable<SendNotificationResponseDto> {
    const payload = {
      ...body,
      scheduledDate: this.toUtcIsoString(body.scheduledDate)
    };

    return this.http.post<SendNotificationResponseDto>(
      `${this.base}/notifications/send`,
      payload
    );
  }

  getNotificationHistory(
    searchParams: NotificationHistorySearchDto = {}
  ): Observable<PagedResultDto<AdminNotificationDto>> {
    const params = this.buildParams({
      ...searchParams,
      sentDateFrom: this.toUtcIsoString(searchParams.sentDateFrom),
      sentDateTo: this.toUtcIsoString(searchParams.sentDateTo)
    });

    return this.http.get<PagedResultDto<AdminNotificationDto>>(
      `${this.base}/notifications/history`,
      { params }
    );
  }

  searchContractors(
    searchParams: ContractorAdminSearchDto = {}
  ): Observable<PagedResultDto<ContractorAdminDto>> {
    const params = this.buildParams({
      ...searchParams,
      registeredDateFrom: this.toUtcIsoString(searchParams.registeredDateFrom),
      registeredDateTo: this.toUtcIsoString(searchParams.registeredDateTo)
    });

    return this.http.get<PagedResultDto<ContractorAdminDto>>(
      `${this.base}/contractors`,
      { params }
    );
  }

  getContractorDetail(id: string): Observable<ContractorAdminDetailDto> {
    return this.http.get<ContractorAdminDetailDto>(
      `${this.base}/contractors/${id}`
    );
  }

  updateContractorStatus(
    id: string,
    body: UpdateStatusDto
  ): Observable<ContractorStatusUpdateResultDto> {
    const payload = {
      ...body,
      effectiveDate: this.toUtcIsoString(body.effectiveDate)
    };

    return this.http.patch<ContractorStatusUpdateResultDto>(
      `${this.base}/contractors/${id}/status`,
      payload
    );
  }

  addContractorNote(
    id: string,
    body: CreateAdminNoteDto
  ): Observable<AdminNoteDto> {
    return this.http.post<AdminNoteDto>(
      `${this.base}/contractors/${id}/notes`,
      body
    );
  }

  getContractorNotes(contractorId: string): Observable<AdminNoteDto[]> {
    return this.http.get<AdminNoteDto[]>(
      `${this.base}/contractors/${contractorId}/notes`
    );
  }

  resetContractorPassword(
    id: string,
    body: ResetPasswordDto
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.base}/contractors/${id}/reset-password`,
      body
    );
  }

  impersonateContractor(id: string): Observable<ImpersonationResponseDto> {
    return this.http.post<ImpersonationResponseDto>(
      `${this.base}/contractors/${id}/impersonate`,
      {}
    );
  }

  searchFacilities(
    searchParams: FacilityAdminSearchDto = {}
  ): Observable<PagedResultDto<FacilityForAdminDto>> {
    const params = this.buildParams({
      ...searchParams,
      registeredDateFrom: this.toUtcIsoString(searchParams.registeredDateFrom),
      registeredDateTo: this.toUtcIsoString(searchParams.registeredDateTo)
    });

    return this.http.get<PagedResultDto<FacilityForAdminDto>>(
      `${this.base}/facilities`,
      { params }
    );
  }

  getFacilityDetail(id: string): Observable<FacilityAdminDetailDto> {
    return this.http.get<FacilityAdminDetailDto>(
      `${this.base}/facilities/${id}`
    );
  }

  updateFacilityStatus(
    id: string,
    body: UpdateStatusDto
  ): Observable<{ message: string }> {
    const payload = {
      ...body,
      effectiveDate: this.toUtcIsoString(body.effectiveDate)
    };

    return this.http.patch<{ message: string }>(
      `${this.base}/facilities/${id}/status`,
      payload
    );
  }

  addFacilityNote(
    id: string,
    body: CreateAdminNoteDto
  ): Observable<AdminNoteDto> {
    return this.http.post<AdminNoteDto>(
      `${this.base}/facilities/${id}/notes`,
      body
    );
  }

  getFacilityNotes(facilityId: string): Observable<AdminNoteDto[]> {
    return this.http.get<AdminNoteDto[]>(
      `${this.base}/facilities/${facilityId}/notes`
    );
  }

  impersonateFacility(id: string): Observable<ImpersonationResponseDto> {
    return this.http.post<ImpersonationResponseDto>(
      `${this.base}/facilities/${id}/impersonate`,
      {}
    );
  }

  getAdminAlerts(): Observable<AdminAlertDto[]> {
    return this.http.get<AdminAlertDto[]>(`${this.base}/alerts`);
  }

  dismissAlert(id: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      `${this.base}/alerts/${id}/dismiss`,
      {}
    );
  }

  resetFacilityAdminPassword(
    id: string,
    sendEmail = true
  ): Observable<{ message: string; temporaryPassword?: string | null }> {
    const params = this.buildParams({ sendEmail });
    return this.http.post<{
      message: string;
      temporaryPassword?: string | null;
    }>(`/api/facilityadmins/${id}/reset-password`, {}, { params });
  }

  getVerificationTasks(): Observable<VerificationTaskDto[]> {
    return this.http.get<VerificationTaskDto[]>(
      `${this.base}/verification-tasks`
    );
  }

  reviewVerificationTask(
    taskId: string,
    review: ReviewVerificationTaskDto
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.base}/verification-tasks/${taskId}/review`,
      review
    );
  }

  verifyContractor(
    id: string,
    data: VerifyContractorDto
  ): Observable<ContractorVerificationResultDto> {
    return this.http.patch<ContractorVerificationResultDto>(
      `${this.base}/contractors/${id}/verify`,
      data
    );
  }

  approveFacilityOnboarding(
    data: ApproveFacilityOnboardingDto
  ): Observable<ApproveFacilityOnboardingResponseDto> {
    return this.http.post<ApproveFacilityOnboardingResponseDto>(
      `${this.base}/facilities/approve-onboarding`,
      data
    );
  }
}
